import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { formatDate, formatEuro, clienteNome, Modal, ConfirmDialog, LoadingSpinner } from '../components/shared';

// ─── CEDENTI ──────────────────────────────────────────────────────────────────
function CedenteForm({ initial = {}, onSave, onClose, loading }) {
  const [d, setD] = useState({ denominazione: '', cf: '', piva: '', indirizzo: '', quota_perc: '', note: '', ...initial });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(d); }}>
      <div className="form-grid">
        <div className="form-group form-full">
          <label className="form-label">Denominazione / Ragione Sociale *</label>
          <input className="form-control" value={d.denominazione} onChange={e => set('denominazione', e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Codice Fiscale</label>
          <input className="form-control" value={d.cf || ''} onChange={e => set('cf', e.target.value.toUpperCase())} maxLength={16} />
        </div>
        <div className="form-group">
          <label className="form-label">Partita IVA</label>
          <input className="form-control" value={d.piva || ''} onChange={e => set('piva', e.target.value)} maxLength={11} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Indirizzo</label>
          <input className="form-control" value={d.indirizzo || ''} onChange={e => set('indirizzo', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Quota ceduta (%)</label>
          <input className="form-control" type="number" step="0.01" min="0" max="100" value={d.quota_perc || ''} onChange={e => set('quota_perc', e.target.value)} />
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note || ''} onChange={e => set('note', e.target.value)} rows={3} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: '1px solid var(--border)', marginTop: 20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvataggio…' : 'Salva Cedente'}</button>
      </div>
    </form>
  );
}

export function Cedenti({ addToast }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [delItem, setDelItem]   = useState(null);
  const [saving, setSaving]     = useState(false);

  const load = () => { setLoading(true); api.getCedenti().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const handleSave = async (formData) => {
    setSaving(true);
    try { await api.createCedente(formData); addToast('Cedente aggiunto', 'success'); setShowForm(false); load(); }
    catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">Cedenti</div>
          <div className="page-subtitle">Anagrafica cedenti quote societarie</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo Cedente</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <LoadingSpinner /> : (
            <table>
              <thead><tr><th>Denominazione</th><th>CF</th><th>P.IVA</th><th>Indirizzo</th><th>Quota %</th><th>Note</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="fw-500">{r.denominazione}</td>
                    <td className="td-mono">{r.cf || '—'}</td>
                    <td className="td-mono">{r.piva || '—'}</td>
                    <td>{r.indirizzo || '—'}</td>
                    <td>{r.quota_perc != null ? `${r.quota_perc}%` : '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{r.note || '—'}</td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6}><div className="empty-state"><div className="empty-state-icon">📑</div><div className="empty-state-title">Nessun cedente</div></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <Modal title="Nuovo Cedente" onClose={() => setShowForm(false)}>
          <CedenteForm onSave={handleSave} onClose={() => setShowForm(false)} loading={saving} />
        </Modal>
      )}
    </div>
  );
}

// ─── CESSIONE QUOTE ───────────────────────────────────────────────────────────
function CessquoteForm({ initial = {}, clientiList, cedentiList, onSave, onClose, loading }) {
  const [d, setD] = useState({
    id_cedente: '', id_cliente: '', data_atto: '', quota_ceduta: '',
    corrispettivo: '', notaio: '', stato: 'IN_CORSO', note: '', ...initial,
    id_cedente: initial.id_cedente || '',
    id_cliente: initial.id_cliente || '',
    quota_ceduta: initial.quota_ceduta || '',
    corrispettivo: initial.corrispettivo || '',
  });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(d); }}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Cedente</label>
          <select className="form-control" value={d.id_cedente} onChange={e => set('id_cedente', e.target.value)}>
            <option value="">— Seleziona cedente —</option>
            {cedentiList.map(c => <option key={c.id} value={c.id}>{c.denominazione}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Società (Cliente)</label>
          <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
            <option value="">— Seleziona società —</option>
            {clientiList.filter(c => c.tipo === 'PG').map(c => <option key={c.id} value={c.id}>{clienteNome(c)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Data atto</label>
          <input className="form-control" type="date" value={d.data_atto || ''} onChange={e => set('data_atto', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Quota ceduta (%)</label>
          <input className="form-control" type="number" step="0.01" min="0" max="100" value={d.quota_ceduta} onChange={e => set('quota_ceduta', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Corrispettivo (€)</label>
          <input className="form-control" type="number" step="0.01" value={d.corrispettivo} onChange={e => set('corrispettivo', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Notaio</label>
          <input className="form-control" value={d.notaio || ''} onChange={e => set('notaio', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Stato pratica</label>
          <select className="form-control" value={d.stato} onChange={e => set('stato', e.target.value)}>
            <option value="IN_CORSO">In corso</option>
            <option value="COMPLETATA">Registrata</option>
            <option value="ANNULLATA">Annullata</option>
          </select>
        </div>
        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note || ''} onChange={e => set('note', e.target.value)} rows={3} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding: '16px 0 0', borderTop: '1px solid var(--border)', marginTop: 20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Salvataggio…' : 'Salva Cessione'}</button>
      </div>
    </form>
  );
}

export function Cessquote({ addToast }) {
  const [rows, setRows]       = useState([]);
  const [clienti, setClienti] = useState([]);
  const [cedenti, setCedenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([api.getCessquote(), api.getClienti({ limit: 200 }), api.getCedenti()])
      .then(([c, cl, ce]) => { setRows(c); setClienti(cl.rows); setCedenti(ce); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSave = async (formData) => {
    setSaving(true);
    // POST directly to /api/cessquote (not in api.js yet — use fetch)
    try {
      const res = await fetch('/api/cessquote', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(formData) });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      addToast('Cessione creata', 'success'); setShowForm(false); load();
    } catch (e) { addToast(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const totCorrisp = rows.reduce((s, r) => s + (r.corrispettivo || 0), 0);

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">Cessione Quote</div>
          <div className="page-subtitle">Pratiche di cessione quote societarie</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuova Cessione</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
        {[['Pratiche totali', rows.length, 'var(--accent)'], ['Corrispettivi totali', formatEuro(totCorrisp), 'var(--c-green)'], ['In corso', rows.filter(r => r.stato === 'IN_CORSO').length, 'var(--c-amber)']].map(([l, v, c]) => (
          <div key={l} className="kpi-card">
            <div className="kpi-accent" style={{ background: c }} />
            <div className="kpi-label">{l}</div>
            <div className="kpi-value" style={{ fontSize: typeof v === 'string' ? 20 : 30 }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <LoadingSpinner /> : (
            <table>
              <thead><tr><th>Cedente</th><th>Società</th><th>Data Atto</th><th>Quota %</th><th>Corrispettivo</th><th>Notaio</th><th>Stato</th></tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id}>
                    <td className="fw-500">{r.denominazione || '—'}</td>
                    <td>{clienteNome(r) || '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.data_atto ? formatDate(r.data_atto) : '—'}</td>
                    <td>{r.quota_ceduta != null ? `${r.quota_ceduta}%` : '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.corrispettivo ? formatEuro(r.corrispettivo) : '—'}</td>
                    <td>{r.notaio || '—'}</td>
                    <td><span className={`pill ${r.stato === 'COMPLETATA' ? 'pill-green' : r.stato === 'ANNULLATA' ? 'pill-gray' : 'pill-amber'}`}>
                      {r.stato === 'COMPLETATA' ? 'Registrata' : r.stato === 'IN_CORSO' ? 'In corso' : 'Annullata'}
                    </span></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={7}><div className="empty-state"><div className="empty-state-icon">🔄</div><div className="empty-state-title">Nessuna cessione quote</div></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <Modal title="Nuova Cessione Quote" onClose={() => setShowForm(false)} size="lg">
          <CessquoteForm clientiList={clienti} cedentiList={cedenti} onSave={handleSave} onClose={() => setShowForm(false)} loading={saving} />
        </Modal>
      )}
    </div>
  );
}
